import { Box, Group, Stack, Text } from '@mantine/core';
import ChangeIndicator from './ChangeIndicator';

/**
 * Engagement by surface: one horizontal bar per surface, sorted high to low.
 *
 * A donut was the wrong form here and not by a little. The surfaces routinely
 * differ by three orders of magnitude on the same domain — email clicks in the
 * tens of thousands beside product-block clicks in the low hundreds — and in a
 * donut the small one is a sub-pixel sliver. That is precisely backwards: "the
 * product blocks got 123 clicks against 588K page views" is the actionable fact
 * on that screen, and the form was hiding it.
 *
 * One hue rather than a categorical palette, because these are magnitudes of the
 * same measurement rather than distinct series; the label already carries the
 * identity. Bars keep a minimum width so a small value still renders as a mark
 * rather than as nothing.
 *
 * Each bar carries its own delta when `previousValue` is supplied. That is what
 * lets these bars BE the breakdown rather than sit above a grid of tiles
 * repeating the same numbers — the bars rank, the delta says which way each one
 * moved, and neither needed the other's card.
 *
 * @param {Array<{id: string, label: string, value: number, previousValue?: number}>} data
 */
const SurfaceBars = ({ data }) => {
  const sorted = [...(data || [])].sort((a, b) => b.value - a.value);

  if (sorted.length === 0) {
    return null;
  }

  const maxValue = Math.max(...sorted.map((entry) => entry.value));

  return (
    <Stack gap="xs">
      {sorted.map((entry) => {
        const widthPercent = maxValue > 0 ? (entry.value / maxValue) * 100 : 0;

        return (
          <Group key={entry.id} gap="sm" wrap="nowrap">
            <Text size="xs" c="dimmed" w={140} ta="right" truncate>
              {entry.label}
            </Text>
            <Box style={{ flex: 1 }}>
              <Box
                style={{
                  width: `${Math.max(widthPercent, 1)}%`,
                  minWidth: 4,
                  height: 20,
                  borderRadius: 4,
                  backgroundColor: 'var(--mantine-color-primary-filled)',
                }}
              />
            </Box>
            <Text size="xs" w={80} ta="right" fw={600}>
              {entry.value.toLocaleString(navigator.language)}
            </Text>
            {/* Reserves its width whether or not this row has a previous value, so
                the value column stays aligned down a list where only some
                surfaces were measured last period. */}
            <Box w={70}>
              {entry.previousValue !== undefined && (
                <ChangeIndicator value={entry.value} previousValue={entry.previousValue} />
              )}
            </Box>
          </Group>
        );
      })}
    </Stack>
  );
};

export default SurfaceBars;
